import { skipToken } from '@reduxjs/toolkit/query'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import { DateRangePicker } from '../../components/inputs/dateRange'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { AddDiscountModal } from '../../components/modal/app_modals/addDiscountModal'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { TrendText } from '../../components/typography/trendText'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import {
  formatCurrency,
  getPageFromSkip,
  getSkipFromPage,
  parseDate,
} from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useGetDiscountMetricsQuery,
  useGetDiscountsQuery,
} from '../../redux-toolkits/voucher/voucher.slice'
import { SingleDiscount } from '../../redux-toolkits/discounts/discounts.type'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
export const Discounts = () => {
  const [query, setQuery] = useState<QueryProps>()
  const [selected, setSelected] = useState<SingleDiscount>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modalOpen, modal, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { data: discountMetrics, isFetching: fetchingDiscountMetrics } =
    useGetDiscountMetricsQuery(
      query
        ? {
            startDate: query?.startDate,
            endDate: query?.endDate,
          }
        : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const { data, isFetching } = useGetDiscountsQuery(
    query
      ? {
          skip: query.skip,
          limit: query.limit,
          search: query.search,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      search: searchParams.get('search') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-6 justify-center">
        <PageHeader
          title="Discounts"
          className="!my-0 flex-1 min-w-fit whitespace-nowrap"
        />

        <Dropdown
          menuButton={
            <Button
              label={`${
                query?.startDate
                  ? format(query.startDate, 'dd MMM, yyyy')
                  : 'Start date'
              } - ${
                query?.endDate
                  ? format(query.endDate, 'dd MMM, yyyy')
                  : 'End date'
              }`}
              icon="calendar"
              primary={false}
              className="!font-normal !w-full md:max-w-fit !py-0 !px-0"
              variant="plain"
            />
          }
          menuClassName="!min-w-max !border-[0px]"
          customMenuItems={
            <DateRangePicker
              required
              animate
              navLayout="around"
              mode="range"
              selected={{
                from: parseDate(query?.startDate)?.toDate() ?? undefined,
                to: parseDate(query?.endDate)?.toDate() ?? undefined,
              }}
              onSelect={(selected) => {
                if (selected?.from && selected?.to) {
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    startDate: format(selected?.from, 'yyyy-MM-dd'),
                    endDate: format(selected?.to, 'yyyy-MM-dd'),
                    isCustomDateRange: 'true',
                  })
                }
              }}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
          }
          className="ml-auto"
        />
      </div>

      <CountCardContainer className="!my-0">
        <CountCard
          count={discountMetrics?.data?.totalDiscountAmount ?? 0}
          text="Total discount amount"
          isCurrency
          loading={fetchingDiscountMetrics}
        >
          <TrendText
            value={discountMetrics?.data?.totalDiscountAmountChange ?? 0}
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>

        <CountCard
          count={discountMetrics?.data?.totalOrders ?? 0}
          text="Total no of orders with discounts"
          isCurrency={false}
          loading={fetchingDiscountMetrics}
        >
          <TrendText
            value={discountMetrics?.data?.totalNumberOfOrdersChange ?? 0}
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>

        <CountCard
          count={discountMetrics?.data?.totalUsers ?? 0}
          text="Total no of used discounts"
          isCurrency={false}
          loading={fetchingDiscountMetrics}
        >
          <TrendText
            value={
              discountMetrics?.data?.totalNumberOfDiscountedUsersChange ?? 0
            }
            suffixText={
              query?.startDate && query?.endDate ? '' : 'in the past week'
            }
          />
        </CountCard>
      </CountCardContainer>

      <div className="flex-wrap flex items-center gap-4 justify-between">
        <SearchInput
          placeholder="Search"
          searchFunction={(val) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              search: val,
              skip: '0',
            })
          }}
          className="!w-full md:!max-w-[400px]"
          initialValue={searchParams.get('search') || undefined}
        />

        <Button
          size="medium"
          label="Add Discount"
          primary
          onClick={() => {
            handleModalOpen('addDiscountModal')
          }}
          className="max-md:!w-full"
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'DISCOUNT CODE',
            'Type',
            'Minumum amount',
            'Maximum amount',
            'Status',
            'creation date',
            'Actions',
          ]}
          rows={
            data?.data.discounts.map((discount) => {
              return {
                id: discount._id,
                content: [
                  <button
                    key={`${discount._id}-button`}
                    className=""
                    type="button"
                    onClick={() => {
                      setSelected(discount)
                      handleModalOpen('addDiscountModal')
                    }}
                  >
                    {discount.code}
                  </button>,
                  <span key={`${discount._id}-type`} className="capitalize">
                    {discount.type ?? ''}
                  </span>,
                  formatCurrency(discount.minimumAmount),
                  formatCurrency(discount.maximumAmount),
                  <Status
                    key={`${discount._id}-status`}
                    text={discount.status}
                    type={discount.status === 'active' ? 'success' : 'fail'}
                  />,
                  format(new Date(discount.createdAt), 'dd/MM/yyyy h:mma'),
                  <Dropdown
                    key={`${discount._id}-controls`}
                    menuButton={
                      <Icon id="ellipses" height={18} width={18} className="" />
                    }
                    menuItems={[
                      {
                        name: 'Edit',
                        value: 'edit',
                        onClick: () => {
                          setSelected(discount)
                          handleModalOpen('addDiscountModal')
                        },
                      },
                    ]}
                  />,
                ],
              }
            }) ?? []
          }
          loading={isFetching}
          emptyText="No discounts found"
          isEmpty={[0, undefined].includes(data?.data?.discounts?.length)}
          currentPage={getPageFromSkip(
            Number(query?.skip),
            Number(query?.limit),
          )}
          totalDataCount={data?.data.count}
          pageLimit={Number(query?.limit)}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              skip: `${getSkipFromPage(page, Number(query?.limit))}`,
            })
          }}
        />
      </div>

      <AddDiscountModal
        open={modalOpen && modal === 'addDiscountModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelected(undefined)
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        edit={selected !== undefined}
        discountId={selected?._id}
      />
    </div>
  )
}
